import React, { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useCredits } from '../contexts/CreditsContext';
import '../styles/PaymentResult.css';

export const PaymentSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { fetchCredits, credits } = useCredits();
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creditsAdded, setCreditsAdded] = useState<number | null>(null);
  const hasVerified = useRef(false);

  useEffect(() => {
    // Prevent multiple verification attempts
    if (hasVerified.current) {
      return;
    }
    
    const verifyPayment = async () => {
      const sessionId = searchParams.get('session_id');
      
      if (!sessionId) {
        setError('No session ID found');
        setVerifying(false);
        return;
      }

      // Mark as verified to prevent re-runs
      hasVerified.current = true;

      try {
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
        const storedTokens = localStorage.getItem('authTokens');
        const token = storedTokens ? JSON.parse(storedTokens).idToken : null;

        const response = await fetch(
          `${apiUrl}/api/payments/verify?session_id=${sessionId}`,
          {
            headers: {
              'Content-Type': 'application/json',
              ...(token && { Authorization: `Bearer ${token}` })
            }
          }
        );

        const data = await response.json();

        if (data.success && data.paid) {
          setCreditsAdded(data.creditsAdded);
          // Refresh credits balance
          await fetchCredits();
        } else {
          setError(data.message || data.error || 'Payment verification failed');
        }
      } catch (err) {
        setError('Failed to verify payment');
        console.error('Payment verification error:', err);
      } finally {
        setVerifying(false);
      }
    };

    verifyPayment();
  }, [searchParams]); // Remove fetchCredits from dependencies

  if (verifying) {
    return (
      <div className="payment-result">
        <div className="payment-result__card">
          <div className="payment-result__icon loading">⏳</div>
          <h1>Verifying Payment...</h1>
          <p>Please wait while we confirm your payment.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-result">
        <div className="payment-result__card error">
          <div className="payment-result__icon">⚠️</div>
          <h1>Verification Issue</h1>
          <p>{error}</p>
          <p className="payment-result__note">
            If you were charged, your credits will be added automatically. 
            Please check your dashboard or contact support if the issue persists.
          </p>
          <div className="payment-result__actions">
            <Link to="/dashboard" className="btn-primary">Go to Dashboard</Link>
            <Link to="/help" className="btn-secondary">Contact Support</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-result">
      <div className="payment-result__card success">
        <div className="payment-result__icon">✅</div>
        <h1>Payment Successful!</h1>
        <p className="payment-result__message">
          Thank you for your purchase!
        </p>
        
        {creditsAdded && (
          <div className="payment-result__credits">
            <span className="credits-added">+{creditsAdded}</span>
            <span className="credits-label">credit{creditsAdded > 1 ? 's' : ''} added</span>
          </div>
        )}

        {credits && (
          <div className="payment-result__balance">
            <span>Your new balance:</span>
            <strong>{credits.creditBalance} credit{credits.creditBalance !== 1 ? 's' : ''}</strong>
          </div>
        )}

        <div className="payment-result__actions">
          <Link to="/dashboard" className="btn-primary">Start Writing</Link>
          <Link to="/pricing" className="btn-secondary">Buy More Credits</Link>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
